/**
 * SessionLengthPicker — Pre-treatment step 1.
 *   Therapist picks the planned length of today's session.
 *   Preset chips (30 / 60 / 90 / 120) + custom number override.
 *
 * Props:
 *   value: number | null
 *   onChange: (next: number | null) => void
 *   onSaveDraft?: () => void
 *   testIdPrefix?: string
 */
const PRESETS = [30, 60, 90, 120];

export default function SessionLengthPicker({ value, onChange, onSaveDraft, testIdPrefix = "session-length" }) {
  const isPreset = PRESETS.includes(value);
  return (
    <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8" data-testid={`${testIdPrefix}-section`}>
      <div className="label-overline mb-3 text-terracotta-600">Planned length</div>
      <p className="text-sm text-ink-700 mb-4">Tap a preset, or enter a custom number of minutes.</p>
      <div className="flex flex-wrap gap-2 mb-4" data-testid={`${testIdPrefix}-presets`}>
        {PRESETS.map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => { onChange(m); onSaveDraft?.(); }}
            data-testid={`${testIdPrefix}-preset-${m}`}
            className={`min-h-[48px] px-5 rounded-full border text-sm font-medium transition-all ${
              value === m
                ? "bg-sage-500 text-white border-sage-500"
                : "bg-white border-paper-200 text-ink-700 hover:border-sage-500/40"
            }`}
          >
            {m} min
          </button>
        ))}
      </div>
      <div className="flex items-center gap-3">
        <label className="label-overline">Custom</label>
        <input
          type="number"
          min={0}
          max={300}
          value={value == null ? "" : value}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") return onChange(null);
            const n = Math.max(0, Math.min(300, parseInt(raw) || 0));
            onChange(n);
          }}
          onBlur={() => onSaveDraft?.()}
          data-testid={`${testIdPrefix}-custom-input`}
          className="w-28 min-h-[44px] px-3 rounded-xl bg-paper-50 border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none text-sm"
          placeholder="e.g. 75"
        />
        <span className="text-sm text-ink-500">minutes{!isPreset && value != null ? " (custom)" : ""}</span>
      </div>
    </div>
  );
}
